import { type ChangeEvent, useMemo, useState } from 'react'
import { type FieldErrors, type UseFormRegister } from 'react-hook-form'

import {
  FormSelectFieldDS,
  FormTextFieldDS,
} from 'components/reusable/form/FormFields'
import { CollapsibleContainer, SolidCircleCheckIcon } from 'slab'
import { formatSSN, snakeToSentenceCase } from 'utils'
import { MemberOnboardingMemberOccupation } from 'utils/web_api.schemas'

import { useOnboarding } from '../OnboardingContext'
import { type ConfirmEnrollmentFormData } from '../types'

export function ConfirmEnrollmentMemberCard ({
  register,
  errors,
  isFormSubmitting,
  isEverythingValid,
  genderOptions,
  validateUniqueSSN,
}: {
  register: UseFormRegister<ConfirmEnrollmentFormData>;
  errors: FieldErrors<ConfirmEnrollmentFormData>;
  isFormSubmitting: boolean;
  isEverythingValid: boolean;
  genderOptions: { value: string; label: string }[];
  validateUniqueSSN?: (value: string) => string | boolean;
}) {
  const { isLoading } = useOnboarding()
  const [open, setOpen] = useState<boolean>(true)

  const occupationOptions = useMemo(
    () =>
      Object.values(MemberOnboardingMemberOccupation).map((occupation) => ({
        value: occupation,
        label: snakeToSentenceCase(occupation),
      })),
    []
  )

  const citizenshipStatusOptions = useMemo(
    () => [
      {
        value: 'true',
        label: 'American citizen',
      },
      {
        value: 'false',
        label: 'Not an American citizen',
      },
    ],
    []
  )

  return (
    <CollapsibleContainer
      open={open}
      onOpenChange={setOpen}
      heading='You'
      headingIconContent={
        isEverythingValid && (
          <div className='text-green-400'>
            <SolidCircleCheckIcon aria-label='(complete)' size={16} />
          </div>
        )
      }
    >
      <div className='p-4 space-y-6'>
        <FormSelectFieldDS<ConfirmEnrollmentFormData>
          fieldId='member.gender'
          fieldLabel='Gender'
          description={
            genderOptions.length === 3
              ? "We'll use the gender you specify wherever possible, but are limited to the options that insurance companies can usually accept (M / F / NB)."
              : "While we recognize that gender is a spectrum, some insurance companies' systems require that we use binary sex (M / F)."
          }
          fieldOptions={genderOptions}
          register={register}
          placeholder='Select an option'
          error={errors.member?.gender}
          disabled={isLoading || isFormSubmitting}
          validationRule={{
            required: 'Gender is required',
          }}
        />
        <FormSelectFieldDS<ConfirmEnrollmentFormData>
          fieldId='member.occupation'
          fieldLabel='Occupation'
          fieldOptions={occupationOptions}
          register={register}
          placeholder='Select an option'
          error={errors.member?.occupation}
          disabled={isLoading || isFormSubmitting}
          validationRule={{
            required: 'Occupation is required',
          }}
        />
        <FormSelectFieldDS<ConfirmEnrollmentFormData>
          fieldId='member.is_us_citizen'
          fieldLabel='Citizenship status'
          fieldOptions={citizenshipStatusOptions}
          register={register}
          placeholder='Select an option'
          error={errors.member?.is_us_citizen}
          disabled={isLoading || isFormSubmitting}
          validationRule={{
            required: 'Citizenship status is required',
          }}
        />
        <FormTextFieldDS<ConfirmEnrollmentFormData>
          fieldId='member.ssn'
          fieldLabel='SSN or ITIN'
          register={register}
          placeholder='XXX-XX-XXXX'
          error={errors.member?.ssn}
          disabled={isLoading || isFormSubmitting}
          validationRule={{
            required: 'SSN or ITIN is required',
            pattern: {
              value: /^\d{3}-?\d{2}-?\d{4}$/,
              message: 'Please enter a valid SSN or ITIN (XXX-XX-XXXX)',
            },
            validate: validateUniqueSSN, // Note: This has the side effect of calling trigger() on other SSN fields
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              e.target.value = formatSSN(e.target.value)
            },
          }}
        />
      </div>
    </CollapsibleContainer>
  )
}
